import { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, Bell } from 'lucide-react';
import { BottomSheet } from './Overlay';
import api from '../lib/api';
import { VENUE_TYPES } from '../lib/venueTypes';

/**
 * VenueInviteSheet — Host invites a venue not yet on ASAP.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - defaultVenueName (string)
 *  - onInvited (invite) — callback after success
 */
export default function VenueInviteSheet({ open, onClose, defaultVenueName = '', onInvited }) {
  const [name, setName] = useState(defaultVenueName);
  const [venueType, setVenueType] = useState('');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // {already_invited, email_sent, email_error, ...}

  const reset = () => {
    setName(''); setVenueType(''); setEmail(''); setContact(''); setNote('');
    setError(''); setResult(null);
  };

  const submit = async () => {
    setError('');
    if (!name.trim()) return setError('Venue name is required');
    if (!venueType) return setError('Pick a venue type');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setError('A valid email is required');
    setBusy(true);
    try {
      const { data } = await api.post('/venue-invites', {
        venue_name: name.trim(),
        venue_type: venueType,
        contact_email: email.trim(),
        contact_person: contact.trim(),
        personal_note: note.trim(),
      });
      setResult(data);
      onInvited?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to send invitation. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ── Success / Already-invited result view ──
  if (result) {
    const alreadyInvited = result.already_invited === true;
    const emailOk = result.email_sent === true;
    return (
      <BottomSheet
        open={open}
        onClose={() => { reset(); onClose?.(); }}
        title={alreadyInvited ? 'Already invited' : (emailOk ? 'Invitation sent' : 'Invitation saved')}
        footer={
          <button
            data-testid="venue-invite-done"
            onClick={() => { reset(); onClose?.(); }}
            className="w-full bg-[#111] text-white font-bold rounded-full py-3 text-sm"
          >
            Done
          </button>
        }
      >
        <div data-testid="venue-invite-result" className="text-center py-2">
          {alreadyInvited ? (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#FFC107]/15 text-[#7A4F00] flex items-center justify-center mb-3">
                <Bell size={24} />
              </div>
              <p data-testid="venue-invite-already" className="text-sm font-bold text-[#111]">This venue was already invited</p>
              <p className="text-xs text-[#6B6E73] mt-1">
                Status: <span data-testid="venue-invite-status" className="font-semibold text-[#3F5200] capitalize">{result.status || 'pending'}</span>
              </p>
              <p className="text-[11px] text-[#9B9EA3] mt-3 leading-relaxed">
                To avoid spamming venues, we don't send repeated invites. We'll notify you here when they join.
              </p>
            </>
          ) : emailOk ? (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#B6E600] text-[#111] flex items-center justify-center mb-3">
                <CheckCircle2 size={26} strokeWidth={2.5} />
              </div>
              <p className="text-sm font-bold text-[#111]">Invitation sent successfully</p>
              <p className="text-xs text-[#6B6E73] mt-1 leading-relaxed">
                We've emailed <span className="font-semibold">{result.contact_email}</span>.
                You'll earn Community Points when they join.
              </p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#FF3B30]/12 text-[#FF3B30] flex items-center justify-center mb-3">
                <AlertCircle size={24} />
              </div>
              <p className="text-sm font-bold text-[#111]">Saved, but email couldn't be sent</p>
              <p className="text-xs text-[#6B6E73] mt-1 leading-relaxed">
                The invite is logged on our side — our team will retry delivery shortly.
              </p>
              {result.email_error && (
                <details className="mt-3 text-left">
                  <summary className="text-[11px] text-[#9B9EA3] cursor-pointer">Diagnostic</summary>
                  <p data-testid="venue-invite-email-error" className="mt-1 text-[10px] text-[#9B9EA3] break-words">{result.email_error}</p>
                </details>
              )}
            </>
          )}
        </div>
      </BottomSheet>
    );
  }

  // ── Form ──
  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!busy) { reset(); onClose?.(); } }}
      title="Invite a venue to ASAP"
      footer={
        <button
          data-testid="venue-invite-send"
          onClick={submit}
          disabled={busy}
          className="w-full bg-[#B6E600] text-[#111111] font-bold rounded-full py-3 text-sm active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Send size={15} />
          {busy ? 'Sending…' : 'Send invite'}
        </button>
      }
    >
      <div data-testid="venue-invite-sheet" className="space-y-4">
        <p className="text-xs text-[#6B6E73] leading-relaxed">
          Help grow your local community. If this venue joins ASAP, you'll earn
          <strong className="text-[#3F5200]"> Community Points</strong> and recognition
          as an <strong className="text-[#3F5200]">Early Partner Scout</strong>.
        </p>

        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
            Venue name <span className="text-[#FF3B30]">*</span>
          </label>
          <input
            data-testid="venue-invite-name"
            type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Brew Lab"
            className="biz-input"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
            Venue type <span className="text-[#FF3B30]">*</span>
          </label>
          <div className="flex flex-wrap gap-1.5" data-testid="venue-invite-types">
            {VENUE_TYPES.map(t => (
              <button
                key={t} type="button"
                data-testid={`venue-invite-type-${t.replace(/[^a-zA-Z]/g, '-')}`}
                onClick={() => setVenueType(t)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${venueType === t ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
            Contact email <span className="text-[#FF3B30]">*</span>
          </label>
          <input
            data-testid="venue-invite-email"
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="hello@brewlab.ae"
            className="biz-input"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
            Contact person <span className="text-[#B0B3B8] normal-case">(optional)</span>
          </label>
          <input
            data-testid="venue-invite-contact"
            type="text" value={contact} onChange={e => setContact(e.target.value)}
            placeholder="Manager's name"
            className="biz-input"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
            Personal note <span className="text-[#B0B3B8] normal-case">(optional)</span>
          </label>
          <textarea
            data-testid="venue-invite-note"
            value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={500}
            placeholder="What makes them perfect for ASAP groups?"
            className="biz-input resize-none"
          />
        </div>

        {error && (
          <div data-testid="venue-invite-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl px-3 py-2 text-[#FF3B30] text-xs">
            {error}
          </div>
        )}
      </div>
      <style>{`
        .biz-input {
          width: 100%; background:#fff; color:#111; border-radius:14px;
          padding:10px 12px; font-size:14px; border:1px solid rgba(0,0,0,0.06);
        }
        .biz-input:focus { outline:none; border-color: rgba(182,230,0,0.45); }
      `}</style>
    </BottomSheet>
  );
}
